import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactLoading from "react-loading";
import CommonBtn from "../../components/buttons/commonBtn";
import { config } from "../../config";
import { StatusContext } from "../../contexts/Status.context";
import { UserInfoContext } from "../../contexts/UserInfo.context";
import "./test.css";
import { toast } from "react-toastify";
import useTranslation from "../../hooks/useTranslation";
import ReactPlayer from "react-player";
import { getDateTime } from "../../utils";
import { apiService } from "../../services/api.service";

const emotions = [
  "happiness",
  "contempt",
  "disgust",
  "anger",
  "sadness",
  "fear",
  "surprise",
  "neutral",
];

const MechanicsFree: React.FC = () => {
  const { problemNumber, setProblemNumber, metvProblems, pageStatus, module } =
    useContext(StatusContext);

  const { userId, email, ip } = useContext(UserInfoContext);

  const { t } = useTranslation();

  const playerRef = useRef<ReactPlayer>(null);

  const {
    score,
    setScore,
    scoresDetail,
    setScoresDetail,
    problemScoreDetail,
    setProblemScoreDetail,
  } = useContext(UserInfoContext);

  const [isStartTest, setIsStartTest] = useState(false);
  const [video, setVideo] = useState("");
  const [answer, setAnswer] = useState("");
  const [answers, setAnswers] = useState<string[]>([]);
  const [isNext, setIsNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);

  const videoAttributes = `playsinline autoplay ${isMuted ? "muted" : ""}`;

  const getProblem = useCallback(() => {
    setIsLoading(true);
    const problem = metvProblems[problemNumber - 1];
    setProblemScoreDetail(problemScoreDetail + problem.answer + "~");
    if (problem) {
      setVideo(problem.video);
      setAnswer(problem.answer);
    }
    setIsLoading(false);
  }, [metvProblems, problemNumber]);

  useEffect(() => {
    if (problemNumber > 0) getProblem();
  }, [problemNumber]);

  useEffect(() => {
    handleVideoBlockHeight();
    window.addEventListener("resize", handleVideoBlockHeight);
    return () => {
      window.removeEventListener("resize", handleVideoBlockHeight);
    };
  }, []);

  const handleVideoBlockHeight = () => {
    let videoBlock = document.getElementById("video-block");
    let videoBlockWidth =
      document.getElementById("video-block")?.clientWidth || 0;
    if (videoBlock) {
      videoBlock.style.height = videoBlockWidth * (324.34 / 429.8) + 4 + "px";
    }
  };

  const handleStart = async () => {
    try {
      await handleStartScore();
      setIsStartTest(true);
      handleNextProblem();
      if (
        pageStatus == "Practice - speaking" ||
        pageStatus == "Practice - for beginners"
      ) {
        setTimeout(() => {
          setIsMuted(false);
        }, 500);
      }
    } catch (error) {
      toast.error("Failed to get problems.");
    }
  };

  const handleStartScore = async () => {
    try {
      const dateTime = getDateTime();
      const data = `score=${dateTime},${userId.replaceAll(
        ",",
        " "
      )},${email},${ip},Started,${module}`;
      const result = await apiService.saveMainScore(data);
    } catch (error) {}
  };

  const handleNextProblem = () => {
    setProblemNumber(problemNumber + 1);
    setAnswers([]);
    setIsNext(false);
    handleVideo("hideControls");
  };

  const handleAnswer = (emotion: string) => {
    let newAnswers = [...answers, emotion];
    setAnswers(newAnswers);
    handleVideo("");
    if (newAnswers.find((item) => answer === item) || newAnswers.length === 3) {
      handleScore(newAnswers);
      setIsNext(true);
      handleVideo("showControls");
      if (!newAnswers.find((item) => answer === item)) {
        newAnswers = [...newAnswers, answer];
        setAnswers(newAnswers);
      }
    }
  };

  const handleScore = (answers: string[]) => {
    let problemScore = 0;
    if (answers.find((item) => item === answer)) {
      if (answers.length === 1) {
        problemScore = 5;
        setProblemScoreDetail(problemScoreDetail + answers[0] + "~ ~ ~5~");
      } else if (answers.length === 2) {
        problemScore = 1;
        setProblemScoreDetail(
          problemScoreDetail + answers[0] + "~" + answers[1] + "~ ~1~"
        );
      }
      if (answers.length === 3)
        setProblemScoreDetail(
          problemScoreDetail +
            answers[0] +
            "~" +
            answers[1] +
            "~" +
            answers[2] +
            "~0~"
        );
    } else {
      problemScore = -2;
      setProblemScoreDetail(
        problemScoreDetail +
          answers[0] +
          "~" +
          answers[1] +
          "~" +
          answers[2] +
          "~-2~"
      );
    }
    setScore(score + problemScore);
    setScoresDetail({
      ...scoresDetail,
      [answer]: (scoresDetail[answer] || 0) + problemScore,
    });
  };

  const checkStatus = (emotion: string) => {
    if (answers.find((answer) => emotion === answer)) {
      if (emotion === answer) return 2;
      else return 1;
    } else {
      return 0;
    }
  };

  const handleVideo = (mode: string) => {
    let video: any = document.getElementById("test-metv");
    if (mode === "showControls") {
      video.controls = true;
    }
    if (mode === "hideControls") {
      video.controls = false;
    }
    video.currentTime = 0;
    video.play();
  };

  const getProblemState = () => {
    if (answers.length === 0) {
      return {
        text: t("What emotion does this person experience?"),
        color: "white",
      };
    } else if (!answers.find((item) => item === answer) && answers.length < 3) {
      return {
        text: `${t("Attempts left")}: ${3 - answers.length}. ${t("Try again")}`,
        color: "red",
      };
    } else if (answers.find((item) => item === answer)) {
      if (answers.length === 4) {
        return {
          text: `${t("Correct answer")}: ${answer}. ${t(
            "Replay or click next"
          )}`,
          color: "white",
        };
      } else {
        return {
          text: t("Correct! Replay the video or click next"),
          color: "green",
        };
      }
    }
  };

  return (
    <div className="block lg:flex lg:w-[80%] xl:w-[60%] lg:mx-auto">
      <div className="lg:w-[70%]">
        <div className="relative">
          <div
            className="w-[97%] md:w-[70%] lg:w-full mx-auto mt-2 border-4 border-gray-500 rounded mb-8 bg-gray-600"
            id="video-block"
            dangerouslySetInnerHTML={{
              __html: `
                <video ${videoAttributes} disablePictureInPicture controlsList="nodownload" style="width: 100%" id="test-metv">
                  <source src="${
                    isStartTest && config.awsS3CDN + video
                  }" type="video/mp4" />
                  Your browser does not support HTML5 video.
                </video>
              `,
            }}
          />
          {isLoading && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <ReactLoading type="spokes" />
            </div>
          )}
        </div>
        {!isStartTest ? (
          <div className="w-[70%] lg:w-full text-center mx-auto">
            <CommonBtn status={3} onClick={handleStart}>
              {t("Click to start")}
            </CommonBtn>
          </div>
        ) : (
          <div>
            <div className="problem-state w-[70%] lg:w-full mx-auto">
              {problemNumber}/10&nbsp;&nbsp;&nbsp;
              <span style={{ color: getProblemState()?.color }}>
                {getProblemState()?.text}
              </span>
            </div>
            <div className="mt-4 text-center">
              <button
                className={`common-btn common-btn-status-${3} full-metv-training`}
                onClick={() => {
                  window.open(
                    "http://www.microexpressionstrainingvideos.com/pricing/",
                    "_blank"
                  );
                }}
              >
                {t("Full METV Training")}
              </button>
              <a
                href="http://www.microexpressionstrainingvideos.com/pricing/"
                target="_blank"
              >
                <div className="mt-1 hover:text-orange-500">
                  &gt;&gt; Buy our full Micro Expressions Training program to
                  exercise with 300+ videos
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
      <div className="lg:w-[30%] mt-4 lg:mt-0">
        {isStartTest && (
          <div className="grid grid-cols-2 w-[70%] mx-auto">
            {emotions.map((emotion: string, index: number) => (
              <div
                key={index}
                className="mb-2 mx-1 col-span-1 lg:col-span-2 flex justify-center lg:justify-start"
              >
                <CommonBtn
                  status={checkStatus(emotion)}
                  onClick={() => handleAnswer(emotion)}
                  disabled={isNext || isLoading}
                >
                  {t(emotion)}
                </CommonBtn>
              </div>
            ))}

            {isNext ? (
              <div className="mt-2 flex justify-center col-span-2 lg:justify-start">
                <CommonBtn status={3} onClick={handleNextProblem}>
                  {t("next")}
                </CommonBtn>
              </div>
            ) : (
              <div className="md:hidden h-[57px]"></div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MechanicsFree;
